import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ParsedSSEMessage } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { getBufferReader } from '../read-json-stream/utils';

// Internal type for raw parsing (before JSON.parse of data field)
type RawParsedSSEMessage = {
	eventType?: string;
	data?: string;
	id?: string;
};

const parseSSEMessage = (message: string): RawParsedSSEMessage => {
	const parsed: RawParsedSSEMessage = {};
	const dataLines: string[] = [];

	for (const line of message.split('\n')) {
		const trimmedLine = line.trim();
		const colonIndex = trimmedLine.indexOf(':');

		if (trimmedLine && colonIndex !== -1) {
			const field = trimmedLine.substring(0, colonIndex);
			const value = trimmedLine.substring(colonIndex + 1).trimStart();

			switch (field) {
				case 'event':
					parsed.eventType = value;
					break;
				case 'data':
					dataLines.push(value);
					break;
				case 'id':
					parsed.id = value;
					break;
				default:
					break;
			}
		}
	}

	if (dataLines.length) {
		parsed.data = dataLines.join('\n');
	}

	return parsed;
};

/**
 * Reads an SSE stream from a Response object.
 * Always yields ParsedSSEMessage<T> with id, eventType, and data.
 *
 * @param response - The fetch Response to read from
 */
export async function* readSSEStream<T>(response: Response): AsyncGenerator<ParsedSSEMessage<T>> {
	const reader = getBufferReader(response);
	let doneStreaming = false;
	let remainder = '';
	const reliableContracts = fg('rovo_ai_mate_reliability_contracts');
	let pendingCarriageReturn = false;

	try {
		do {
			try {
				const { value, done } = await reader.read();

				doneStreaming = done;
				let text = value ?? '';
				if (reliableContracts) {
					// Preserve a trailing CR until the next read so a split CRLF is one
					// newline, not an accidental event boundary. Lone CR is also valid SSE.
					text = `${pendingCarriageReturn ? '\r' : ''}${text}`;
					pendingCarriageReturn = !done && text.endsWith('\r');
					if (pendingCarriageReturn) {
						text = text.slice(0, -1);
					}
					text = text.replace(/\r\n?/g, '\n');
				}
				if (text) {
					const combined = `${remainder}${text}`;
					const parts = combined.split('\n\n'); // SSE messages are separated by double newlines
					// the last part may be incomplete — keep it for the next read
					remainder = parts.pop() ?? '';

					for (const chunk of parts) {
						try {
							const parsed = parseSSEMessage(chunk);
							if (parsed.data) {
								const parsedData = JSON.parse(parsed.data) as T;
								yield {
									__isSSEWrapper: true as const,
									data: parsedData,
									id: parsed.id,
									eventType: parsed.eventType,
								};
							}
						} catch {
							// ignore malformed JSON; keep streaming future valid messages
						}
					}
				}

				// If the stream just ended and we still have a remainder, try to parse it once.
				if (doneStreaming && remainder) {
					try {
						const parsed = parseSSEMessage(remainder);
						if (parsed.data) {
							const parsedData = JSON.parse(parsed.data) as T;
							yield {
								__isSSEWrapper: true as const,
								data: parsedData,
								id: parsed.id,
								eventType: parsed.eventType,
							};
						}
					} catch {
						// ignore malformed tail
					} finally {
						remainder = '';
					}
				}
			} catch (e) {
				// If we abort the call, this exception bubbles up, so we want to catch it and exit gracefully
				if (e instanceof DOMException && e.name === 'AbortError') {
					doneStreaming = true;
				} else {
					throw e;
				}
			}
		} while (!doneStreaming);
	} finally {
		if (reliableContracts) {
			try {
				if (!doneStreaming) {
					await reader.cancel();
				}
			} catch {
				// Cleanup must not replace the original read error or consumer outcome.
			} finally {
				reader.releaseLock();
			}
		}
	}
}
